import { useTranslation } from 'react-i18next';
import { ArrowLeft, Shield, Mail, Trash2, Database, Share2, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LanguageSelector from '@/components/LanguageSelector';

export default function PrivacyPolicy() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language || 'es';

  const content = {
    es: {
      title: 'Política de Privacidad',
      lastUpdated: 'Última actualización: 12 de enero de 2026',
      intro: 'En Motivator, nos comprometemos a proteger tu privacidad. Esta política describe cómo recopilamos, usamos y protegemos tu información personal en Motivator.',
      sections: [
        {
          icon: Database,
          title: '1. Datos que recopilamos',
          content: `Recopilamos únicamente los datos necesarios para el funcionamiento de la aplicación:
          
• **Datos de cuenta**: Email, nombre y foto de perfil (proporcionados por Google al iniciar sesión)
• **Datos de uso**: Tareas, premios, puntos, listas creadas y compartidas
• **Datos técnicos**: Suscripciones a notificaciones push (si las activas)

**No recopilamos**: ubicación, contactos, archivos personales, datos financieros ni información sensible.`
        },
        {
          icon: Share2,
          title: '2. Cómo usamos tus datos',
          content: `Usamos tus datos exclusivamente para:

• Proporcionar y mantener el servicio de Motivator
• Permitir la colaboración con otros usuarios (listas compartidas)
• Enviar notificaciones push (solo si las activas)
• Mejorar la experiencia de usuario

**No vendemos** ni compartimos tus datos con terceros para fines publicitarios.`
        },
        {
          icon: Lock,
          title: '3. Seguridad de los datos',
          content: `Protegemos tus datos mediante:

• Conexiones cifradas (HTTPS)
• Autenticación segura a través de Google OAuth
• Almacenamiento seguro en servidores protegidos
• Acceso restringido solo a personal autorizado`
        },
        {
          icon: Trash2,
          title: '4. Tus derechos (RGPD)',
          content: `Tienes derecho a:

• **Acceder** a tus datos personales
• **Rectificar** datos incorrectos
• **Eliminar** tu cuenta y todos tus datos
• **Exportar** tus datos en formato legible
• **Retirar** el consentimiento en cualquier momento

Para ejercer estos derechos, puedes eliminar tu cuenta desde la configuración de la app o contactarnos en info@motivator.es`
        },
        {
          icon: Shield,
          title: '5. Retención de datos',
          content: `• Mantenemos tus datos mientras tu cuenta esté activa
• Al eliminar tu cuenta, todos tus datos se borran permanentemente en un plazo máximo de 30 días
• Los datos de listas compartidas permanecen para otros usuarios, pero se elimina tu asociación`
        }
      ],
      contact: {
        title: '6. Contacto',
        content: 'Para cualquier consulta sobre privacidad:',
        company: 'Motivator',
        email: 'info@motivator.es',
        website: 'https://motivator.es'
      },
      deleteAccount: 'Eliminar mi cuenta',
      backToApp: 'Volver a la app'
    },
    en: {
      title: 'Privacy Policy',
      lastUpdated: 'Last updated: January 12, 2026',
      intro: 'At Motivator, we are committed to protecting your privacy. This policy describes how we collect, use, and protect your personal information in Motivator.',
      sections: [
        {
          icon: Database,
          title: '1. Data we collect',
          content: `We collect only the data necessary for the application to function:
          
• **Account data**: Email, name, and profile picture (provided by Google when signing in)
• **Usage data**: Tasks, rewards, points, created and shared lists
• **Technical data**: Push notification subscriptions (if you enable them)

**We do not collect**: location, contacts, personal files, financial data, or sensitive information.`
        },
        {
          icon: Share2,
          title: '2. How we use your data',
          content: `We use your data exclusively to:

• Provide and maintain the Motivator service
• Enable collaboration with other users (shared lists)
• Send push notifications (only if you enable them)
• Improve user experience

**We do not sell** or share your data with third parties for advertising purposes.`
        },
        {
          icon: Lock,
          title: '3. Data security',
          content: `We protect your data through:

• Encrypted connections (HTTPS)
• Secure authentication via Google OAuth
• Secure storage on protected servers
• Restricted access only to authorized personnel`
        },
        {
          icon: Trash2,
          title: '4. Your rights (GDPR)',
          content: `You have the right to:

• **Access** your personal data
• **Rectify** incorrect data
• **Delete** your account and all your data
• **Export** your data in readable format
• **Withdraw** consent at any time

To exercise these rights, you can delete your account from the app settings or contact us at info@motivator.es`
        },
        {
          icon: Shield,
          title: '5. Data retention',
          content: `• We keep your data while your account is active
• When you delete your account, all your data is permanently deleted within 30 days
• Shared list data remains for other users, but your association is removed`
        }
      ],
      contact: {
        title: '6. Contact',
        content: 'For any privacy inquiries:',
        company: 'Motivator',
        email: 'info@motivator.es',
        website: 'https://motivator.es'
      },
      deleteAccount: 'Delete my account',
      backToApp: 'Back to app'
    },
    gl: {
      title: 'Política de Privacidade',
      lastUpdated: 'Última actualización: 12 de xaneiro de 2026',
      intro: 'En Motivator, comprometémonos a protexer a túa privacidade. Esta política describe como recompilamos, usamos e protexemos a túa información persoal en Motivator.',
      sections: [
        {
          icon: Database,
          title: '1. Datos que recompilamos',
          content: `Recompilamos unicamente os datos necesarios para o funcionamento da aplicación:
          
• **Datos de conta**: Email, nome e foto de perfil (proporcionados por Google ao iniciar sesión)
• **Datos de uso**: Tarefas, premios, puntos, listas creadas e compartidas
• **Datos técnicos**: Subscricións a notificacións push (se as activas)

**Non recompilamos**: ubicación, contactos, arquivos persoais, datos financeiros nin información sensible.`
        },
        {
          icon: Share2,
          title: '2. Como usamos os teus datos',
          content: `Usamos os teus datos exclusivamente para:

• Proporcionar e manter o servizo de Motivator
• Permitir a colaboración con outros usuarios (listas compartidas)
• Enviar notificacións push (só se as activas)
• Mellorar a experiencia de usuario

**Non vendemos** nin compartimos os teus datos con terceiros para fins publicitarios.`
        },
        {
          icon: Lock,
          title: '3. Seguridade dos datos',
          content: `Protexemos os teus datos mediante:

• Conexións cifradas (HTTPS)
• Autenticación segura a través de Google OAuth
• Almacenamento seguro en servidores protexidos
• Acceso restrinxido só a persoal autorizado`
        },
        {
          icon: Trash2,
          title: '4. Os teus dereitos (RXPD)',
          content: `Tes dereito a:

• **Acceder** aos teus datos persoais
• **Rectificar** datos incorrectos
• **Eliminar** a túa conta e todos os teus datos
• **Exportar** os teus datos en formato lexible
• **Retirar** o consentimento en calquera momento

Para exercer estes dereitos, podes eliminar a túa conta desde a configuración da app ou contactarnos en info@motivator.es`
        },
        {
          icon: Shield,
          title: '5. Retención de datos',
          content: `• Mantemos os teus datos mentres a túa conta estea activa
• Ao eliminar a túa conta, todos os teus datos bórranse permanentemente nun prazo máximo de 30 días
• Os datos de listas compartidas permanecen para outros usuarios, pero elimínase a túa asociación`
        }
      ],
      contact: {
        title: '6. Contacto',
        content: 'Para calquera consulta sobre privacidade:',
        company: 'Motivator',
        email: 'info@motivator.es',
        website: 'https://motivator.es'
      },
      deleteAccount: 'Eliminar a miña conta',
      backToApp: 'Volver á app'
    },
    ca: {
      title: 'Política de Privadesa',
      lastUpdated: 'Última actualització: 12 de gener de 2026',
      intro: 'A Motivator, ens comprometem a protegir la teva privadesa. Aquesta política descriu com recopilem, utilitzem i protegim la teva informació personal a Motivator.',
      sections: [
        {
          icon: Database,
          title: '1. Dades que recopilem',
          content: `Recopilem únicament les dades necessàries per al funcionament de l'aplicació:
          
• **Dades de compte**: Email, nom i foto de perfil (proporcionats per Google en iniciar sessió)
• **Dades d'ús**: Tasques, premis, punts, llistes creades i compartides
• **Dades tècniques**: Subscripcions a notificacions push (si les actives)

**No recopilem**: ubicació, contactes, arxius personals, dades financeres ni informació sensible.`
        },
        {
          icon: Share2,
          title: '2. Com utilitzem les teves dades',
          content: `Utilitzem les teves dades exclusivament per:

• Proporcionar i mantenir el servei de Motivator
• Permetre la col·laboració amb altres usuaris (llistes compartides)
• Enviar notificacions push (només si les actives)
• Millorar l'experiència d'usuari

**No venem** ni compartim les teves dades amb tercers per a fins publicitaris.`
        },
        {
          icon: Lock,
          title: '3. Seguretat de les dades',
          content: `Protegim les teves dades mitjançant:

• Connexions xifrades (HTTPS)
• Autenticació segura a través de Google OAuth
• Emmagatzematge segur en servidors protegits
• Accés restringit només a personal autoritzat`
        },
        {
          icon: Trash2,
          title: '4. Els teus drets (RGPD)',
          content: `Tens dret a:

• **Accedir** a les teves dades personals
• **Rectificar** dades incorrectes
• **Eliminar** el teu compte i totes les teves dades
• **Exportar** les teves dades en format llegible
• **Retirar** el consentiment en qualsevol moment

Per exercir aquests drets, pots eliminar el teu compte des de la configuració de l'app o contactar-nos a info@motivator.es`
        },
        {
          icon: Shield,
          title: '5. Retenció de dades',
          content: `• Mantenim les teves dades mentre el teu compte estigui actiu
• En eliminar el teu compte, totes les teves dades s'esborren permanentment en un termini màxim de 30 dies
• Les dades de llistes compartides romanen per a altres usuaris, però s'elimina la teva associació`
        }
      ],
      contact: {
        title: '6. Contacte',
        content: 'Per a qualsevol consulta sobre privadesa:',
        company: 'Motivator',
        email: 'info@motivator.es',
        website: 'https://motivator.es'
      },
      deleteAccount: 'Eliminar el meu compte',
      backToApp: 'Tornar a l\'app'
    },
    zh: {
      title: '隐私政策',
      lastUpdated: '最后更新：2026年1月12日',
      intro: '在 Motivator，我们致力于保护您的隐私。本政策描述了我们如何在 Motivator 中收集、使用和保护您的个人信息。',
      sections: [
        {
          icon: Database,
          title: '1. 我们收集的数据',
          content: `我们仅收集应用程序运行所必需的数据：
          
• **账户数据**：电子邮件、姓名和头像（通过 Google 登录时提供）
• **使用数据**：任务、奖励、积分、创建和共享的列表
• **技术数据**：推送通知订阅（如果您启用）

**我们不收集**：位置、联系人、个人文件、财务数据或敏感信息。`
        },
        {
          icon: Share2,
          title: '2. 我们如何使用您的数据',
          content: `我们仅将您的数据用于：

• 提供和维护 Motivator 服务
• 允许与其他用户协作（共享列表）
• 发送推送通知（仅在您启用时）
• 改善用户体验

**我们不会**出售或与第三方分享您的数据用于广告目的。`
        },
        {
          icon: Lock,
          title: '3. 数据安全',
          content: `我们通过以下方式保护您的数据：

• 加密连接（HTTPS）
• 通过 Google OAuth 进行安全认证
• 在受保护的服务器上安全存储
• 仅授权人员可访问`
        },
        {
          icon: Trash2,
          title: '4. 您的权利（GDPR）',
          content: `您有权：

• **访问**您的个人数据
• **更正**不正确的数据
• **删除**您的账户和所有数据
• **导出**可读格式的数据
• 随时**撤回**同意

要行使这些权利，您可以从应用设置中删除账户或通过 info@motivator.es 联系我们`
        },
        {
          icon: Shield,
          title: '5. 数据保留',
          content: `• 在您的账户处于活动状态时，我们会保留您的数据
• 删除账户时，您的所有数据将在最多30天内永久删除
• 共享列表数据保留给其他用户，但会删除您的关联`
        }
      ],
      contact: {
        title: '6. 联系方式',
        content: '如有任何隐私问题：',
        company: 'Motivator',
        email: 'info@motivator.es',
        website: 'https://motivator.es'
      },
      deleteAccount: '删除我的账户',
      backToApp: '返回应用'
    }
  };

  const c = content[lang] || content.es;

  return (
    <div className="min-h-screen bg-[#FFF9F0] p-4 md:p-6" data-testid="privacy-policy-page">
      {/* Header */}
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm font-bold text-zinc-600 hover:text-zinc-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {c.backToApp}
          </button>
          <LanguageSelector />
        </div>

        {/* Content */}
        <div className="bg-white border-2 border-[#18181B] rounded-2xl hard-shadow p-6 md:p-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-[#FF8BA7]" />
            <h1 className="text-2xl md:text-3xl font-bold">{c.title}</h1>
          </div>
          <p className="text-xs text-zinc-500 mb-6">{c.lastUpdated}</p>
          
          <p className="text-zinc-600 mb-8 leading-relaxed">{c.intro}</p>

          {/* Sections */}
          <div className="space-y-8">
            {c.sections.map((section, index) => (
              <div key={index} className="border-t border-zinc-200 pt-6">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#ADE498]/30 flex items-center justify-center flex-shrink-0">
                    <section.icon className="w-5 h-5 text-[#18181B]" />
                  </div>
                  <h2 className="text-lg font-bold pt-2">{section.title}</h2>
                </div>
                <div className="pl-13 text-sm text-zinc-600 whitespace-pre-line leading-relaxed">
                  {section.content}
                </div>
              </div>
            ))}

            {/* Contact Section */}
            <div className="border-t border-zinc-200 pt-6">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-[#FFC247]/30 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#18181B]" />
                </div>
                <h2 className="text-lg font-bold pt-2">{c.contact.title}</h2>
              </div>
              <div className="pl-13 text-sm text-zinc-600">
                <p className="mb-3">{c.contact.content}</p>
                <div className="bg-zinc-50 rounded-xl p-4 space-y-2">
                  <p><strong>Empresa:</strong> {c.contact.company}</p>
                  <p><strong>Email:</strong> <a href={`mailto:${c.contact.email}`} className="text-blue-600 hover:underline">{c.contact.email}</a></p>
                  <p><strong>Web:</strong> <a href={c.contact.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{c.contact.website}</a></p>
                </div>
              </div>
            </div>
          </div>

          {/* Delete Account Link */}
          <div className="mt-8 pt-6 border-t border-zinc-200">
            <button
              onClick={() => navigate('/delete-account')}
              className="flex items-center gap-2 text-red-600 hover:text-red-700 font-medium text-sm transition-colors"
              data-testid="delete-account-link"
            >
              <Trash2 className="w-4 h-4" />
              {c.deleteAccount}
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-zinc-400 mt-6">
          © 2026 Motivator. {c.title}
        </p>
      </div>
    </div>
  );
}
